'use client'

import { useState, useCallback, useRef } from 'react'
import type { Database } from '@/account'
import type { ChecklistItem } from '@/entries'
import type { CaptureEntryType } from '../types'
import type { MediaEditValues, LabelSuggestion } from '../media-preview/types'
import type { MediaKind } from '@/entries'

type TaskPriority = Database['public']['Enums']['task_priority']
type ProjectCategory = Database['public']['Enums']['project_category']

export interface UnifiedDraftValues {
  content: string
  title: string
  selectedPeopleIds: string[]
  selectedTopicIds: string[]
  selectedProjectId: string | null
  category: ProjectCategory
  priority: TaskPriority
  important: boolean
  dueDate: Date | null
  checklistItems: ChecklistItem[] | null
}

export const DEFAULT_VALUES: UnifiedDraftValues = {
  content: '',
  title: '',
  selectedPeopleIds: [],
  selectedTopicIds: [],
  selectedProjectId: null,
  category: 'personal',
  priority: 'medium',
  important: false,
  dueDate: null,
  checklistItems: null,
}

export interface UserModifiedFields {
  title: boolean
  category: boolean
  priority: boolean
  important: boolean
  dueDate: boolean
  people: boolean
  topics: boolean
  project: boolean
  checklist: boolean
}

export interface FormState extends UnifiedDraftValues {
  error: string | null
  userSelectedType: CaptureEntryType | null
  showAddPerson: boolean
  suggestedPeople: string[]
  addingPerson: string | null
  labelSuggestions: LabelSuggestion[]
  isCheckingLabels: boolean
  aiUpdatedFields: Set<string>
  editedMediaValues: MediaEditValues | undefined
  showManualMediaForm: boolean
  dismissedMediaType: MediaKind | null
  genresInputValue: string
}

export interface FormStateReturn extends FormState {
  setDraftValue: <K extends keyof UnifiedDraftValues>(
    key: K,
    value: UnifiedDraftValues[K],
  ) => void
  resetDraft: () => void
  setError: (error: string | null) => void
  setUserSelectedType: (type: CaptureEntryType | null) => void
  setShowAddPerson: (show: boolean) => void
  setSuggestedPeople: (
    people: string[] | ((prev: string[]) => string[]),
  ) => void
  setAddingPerson: (person: string | null) => void
  setLabelSuggestions: (
    suggestions:
      | LabelSuggestion[]
      | ((prev: LabelSuggestion[]) => LabelSuggestion[]),
  ) => void
  setIsCheckingLabels: (checking: boolean) => void
  setAiUpdatedFields: (fields: Set<string>) => void
  setEditedMediaValues: (
    values:
      | MediaEditValues
      | undefined
      | ((prev: MediaEditValues | undefined) => MediaEditValues | undefined),
  ) => void
  setShowManualMediaForm: (show: boolean) => void
  setDismissedMediaType: (type: MediaKind | null) => void
  setGenresInputValue: (value: string) => void
  /** Read-only access to user modification tracking */
  userModifiedRef: React.MutableRefObject<UserModifiedFields>
  /** Read-only access to title shown tracking */
  hasShownTitleRef: React.MutableRefObject<boolean>
  /** Read-only access to last applied analysis key */
  lastAppliedAnalysisRef: React.MutableRefObject<string | null>
  /** Mark a single field as user-modified */
  markFieldModified: (field: keyof UserModifiedFields) => void
  /** Reset all user-modified tracking to false */
  resetUserModified: () => void
  /** Set the has-shown-title flag */
  setHasShownTitle: (value: boolean) => void
  /** Set the last applied analysis key */
  setLastAppliedAnalysis: (key: string | null) => void
}

interface UseFormStateOptions {
  enforcedProjectId?: string
}

export function useFormState(options?: UseFormStateOptions): FormStateReturn {
  const enforcedProjectId = options?.enforcedProjectId ?? null
  const [draft, setDraft] = useState<UnifiedDraftValues>(() => ({
    ...DEFAULT_VALUES,
    ...(enforcedProjectId ? { selectedProjectId: enforcedProjectId } : {}),
  }))
  const [error, setError] = useState<string | null>(null)
  const [userSelectedType, setUserSelectedType] =
    useState<CaptureEntryType | null>(null)
  const [showAddPerson, setShowAddPerson] = useState(false)
  const [suggestedPeople, setSuggestedPeople] = useState<string[]>([])
  const [addingPerson, setAddingPerson] = useState<string | null>(null)
  const [labelSuggestions, setLabelSuggestions] = useState<LabelSuggestion[]>(
    [],
  )
  const [isCheckingLabels, setIsCheckingLabels] = useState(false)
  const [aiUpdatedFields, setAiUpdatedFields] = useState<Set<string>>(new Set())
  const [editedMediaValues, setEditedMediaValues] = useState<
    MediaEditValues | undefined
  >()
  const [showManualMediaForm, setShowManualMediaForm] = useState(false)
  const [dismissedMediaType, setDismissedMediaType] =
    useState<MediaKind | null>(null)
  const [genresInputValue, setGenresInputValue] = useState<string>('')

  const userModifiedRef = useRef<UserModifiedFields>({
    title: false,
    category: false,
    priority: false,
    important: false,
    dueDate: false,
    people: false,
    topics: false,
    project: false,
    checklist: false,
  })

  // Analysis application tracking
  const lastAppliedAnalysisRef = useRef<string | null>(null)
  const hasShownTitleRef = useRef(false)

  const setDraftValue = useCallback(
    <K extends keyof UnifiedDraftValues>(
      key: K,
      value: UnifiedDraftValues[K],
    ) => {
      setDraft((prev) => ({ ...prev, [key]: value }))
    },
    [],
  )

  const resetDraft = useCallback(() => {
    setDraft({
      ...DEFAULT_VALUES,
      ...(enforcedProjectId ? { selectedProjectId: enforcedProjectId } : {}),
    })
    hasShownTitleRef.current = false
  }, [enforcedProjectId])

  const markFieldModified = useCallback((field: keyof UserModifiedFields) => {
    userModifiedRef.current[field] = true
  }, [])

  const resetUserModified = useCallback(() => {
    userModifiedRef.current = {
      title: false,
      category: false,
      priority: false,
      important: false,
      dueDate: false,
      people: false,
      topics: false,
      project: false,
      checklist: false,
    }
  }, [])

  const setHasShownTitle = useCallback((value: boolean) => {
    hasShownTitleRef.current = value
  }, [])

  const setLastAppliedAnalysis = useCallback((key: string | null) => {
    lastAppliedAnalysisRef.current = key
  }, [])

  return {
    // Draft values
    ...draft,

    // Additional state
    error,
    userSelectedType,
    showAddPerson,
    suggestedPeople,
    addingPerson,
    labelSuggestions,
    isCheckingLabels,
    aiUpdatedFields,
    editedMediaValues,
    showManualMediaForm,
    dismissedMediaType,
    genresInputValue,

    // Setters
    setDraftValue,
    resetDraft,
    setError,
    setUserSelectedType,
    setShowAddPerson,
    setSuggestedPeople,
    setAddingPerson,
    setLabelSuggestions,
    setIsCheckingLabels,
    setAiUpdatedFields,
    setEditedMediaValues,
    setShowManualMediaForm,
    setDismissedMediaType,
    setGenresInputValue,

    // Refs (read-only access)
    userModifiedRef,
    hasShownTitleRef,
    lastAppliedAnalysisRef,

    // Ref setters
    markFieldModified,
    resetUserModified,
    setHasShownTitle,
    setLastAppliedAnalysis,
  }
}
